import {
	EntityError,
	EntityErrorSeverity,
	EntityInstanceErrorCode,
	EntityWithValueState,
	filterOutDuplicateErrors,
	filterOutDuplicateErrorsAsync,
	resolveEntityName,
	TestableEntityState
} from '@elyx-code/project-logic-tree';
import { EditorService } from '../editor';
import { DynamicValueTypes } from '../editor/value-store';
import notifications from '../notifications';
import { YieldOptions, YieldTracker } from '@elyx-code/common-ts-utils';

// Test if the given entry point has all the arguments required to be executed
export function validateLogic(
	entities: TestableEntityState[],
	project: EditorService
): EntityError[] {
	const errors: EntityError[] = entities.reduce((acc, entity) => {
		entity.clearErrors();

		entity.validate();

		const errors = [...entity.getErrors()].filter((error) => {
			if (
				error.code ===
				EntityInstanceErrorCode.LastCalledEntityMustBeReturnEntity
			) {
				return false;
			}

			if (error.code === EntityInstanceErrorCode.MissingRequiredValue) {
				const dynamicValue = project.requestActiveDynamicValue(
					error.entity as EntityWithValueState
				);

				if (dynamicValue?.type === DynamicValueTypes.Missing) {
					return true;
				} else {
					error.entity.errors = error.entity.errors.filter(
						(e) => e.id !== error.id
					);
					notifications.remove(
						project.PROJECT_NOTIFICATIONS_CHANNEL,
						error.id
					);
					return false;
				}
			}

			return error.severity === EntityErrorSeverity.Error;
		});

		return [...acc, ...errors];
	}, [] as EntityError[]);

	return filterOutDuplicateErrors(errors) as EntityError[];
}

export async function validateLogicAsync(
	entities: TestableEntityState[],
	project: EditorService,
	options: YieldOptions = {}
): Promise<EntityError[]> {
	const tracker = YieldTracker.from(options);

	for (const instance of project.logic?.instancesList || []) {
		instance.clearErrors();
		await tracker.tick();
	}

	const missingValues: EntityError[] = [];

	for (const entity of entities) {
		await entity.validateAsync({ tracker });

		const allEntityErrors = await entity.getErrorsAsync({ tracker });

		const entityErrors = allEntityErrors.filter((error) => {
			if (
				error.code ===
				EntityInstanceErrorCode.LastCalledEntityMustBeReturnEntity
			) {
				return false;
			}

			if (error.code === EntityInstanceErrorCode.MissingRequiredValue) {
				const dynamicValue = project.requestActiveDynamicValue(
					error.entity as EntityWithValueState
				);

				if (dynamicValue?.type === DynamicValueTypes.Missing) {
					return true;
				} else {
					console.log(
						'removing error: ',
						error.entity?.type,
						resolveEntityName(error.entity, project.logic),
						error.code
					);
					error.entity.errors = error.entity.errors.filter(
						(e) => e.id !== error.id
					);
					return false;
				}
			}

			return error.severity === EntityErrorSeverity.Error;
		});

		missingValues.push(...entityErrors);

		await tracker.tick();
	}

	return (await filterOutDuplicateErrorsAsync(missingValues, {
		tracker
	})) as EntityError[];
}
